import React, { memo, useState } from "react";
import styles from "./Sidebar.module.scss";
import { getSideBarNavigation } from "@/lib/sidebarNavigation";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearOnLogout, combineClasses } from "@/lib/utils";
import { clear } from "@/store/slices/globalSlice";
import routes from "@/routes/routes";
import LogoutIcon from "@/components/SVGComponents/LogoutIcon";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import type { RootState } from "@/store/rootReducer";
import type { AppDispatch } from "@/store/store";
import type { SidebarItem, SidebarChild } from "@/lib/sidebarNavigation";

// Define props
interface SidebarProps {
  toggleSidebarOpen: (open: boolean) => () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ toggleSidebarOpen }) => {
  const { sidebarOpen } = useSelector((state: RootState) => state.global);
  const [openDropdown, setOpenDropdown] = useState<string>("");

  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // Re-evaluate navigation items on each render so permission changes are reflected
  const navigationItems = getSideBarNavigation();

  const logout = async () => {
    clearOnLogout();
    dispatch(clear());
    navigate(routes?.publicRoutes?.LOGIN?.path ?? "/");
  };

  const toggleDropdown = (id: string) => () => {
    setOpenDropdown((prev) => (prev === id ? "" : id));
  };

  return (
    <div
      className={combineClasses(
        styles.leftSideContainer,
        !sidebarOpen ? styles.sidebarClosed : styles.sidebarOpen
      )}
      onMouseOver={toggleSidebarOpen(true)}
    >
      {/* Logo */}
      <div className={styles.logoContainer}>
        <div className={styles.iconContainer}>
          <div className={styles.mainIconDiv}>
            <img
              src="/logo.png"
              alt="GD logo"
              className={styles.mainIconImage}
            />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className={styles.navContainer}>
        {navigationItems.map((data: SidebarItem) => {
            const Icon = data?.icon;
            const children = data?.children;

            if (children?.length) {
              return (
                <React.Fragment key={data.id}>
                  <div
                    onClick={toggleDropdown(data.id)}
                    className={combineClasses(
                      styles.navLink,
                      !sidebarOpen && styles.sidebarClosed,
                      openDropdown === data.id && styles.activeLink
                    )}
                  >
                    <div className={styles.linksNames}>
                      {sidebarOpen && (
                        <p className={styles.linkPageName}>{data.pageName}</p>
                      )}
                    </div>
                    <ArrowIcon
                      className={styles.arrowIcon}
                      style={{
                        transform:
                          openDropdown === data.id
                            ? "rotate(-90deg)"
                            : "rotate(90deg)",
                        transition: "transform ease 300ms",
                      }}
                    />
                  </div>

                  {openDropdown === data.id && (
                    <div className={styles.leftBorder}>
                      {children.map((child: SidebarChild) => (
                        <NavLink
                          key={child.id}
                          className={({ isActive }) =>
                            `${styles.navLink} ${styles.childNavLink} ${
                              !sidebarOpen ? styles.sidebarClosed : ""
                            } ${isActive && styles.activeLink}`
                          }
                          to={`${data.path}/${child.path}`}
                          state={{ previousPath: location.pathname }}
                        >
                          <div className={styles.linksNames}>
                            {sidebarOpen && (
                              <p className={styles.linkPageName}>
                                {child.pageName}
                              </p>
                            )}
                          </div>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            } else if (!children) {
              const Icon = data?.icon;
              return (
                <NavLink
                  key={data.id}
                  className={({ isActive }) =>
                    `${styles.navLink} ${
                      !sidebarOpen ? styles.sidebarClosed : ""
                    } ${isActive && styles.activeLink}`
                  }
                  to={data.path}
                  state={{ previousPath: location.pathname }}
                  onClick={toggleDropdown("")}
                >
                  <div className={styles.linksNames}>
                    {sidebarOpen && (
                      <>
                        {Icon && <Icon />}
                        <p className={styles.linkPageName}>{data.pageName}</p>
                      </>
                    )}
                  </div>
                </NavLink>
              );
            }
            return null;
          })}
      </div>

      {/* Logout */}
      <div
        className={`${styles.navLink} ${styles.logoutButton} ${
          !sidebarOpen ? styles.sidebarClosed : ""
        }`}
        onClick={logout}
      >
        <div className={styles.linksNames}>
          <LogoutIcon className={styles.logoutBTN} />
          {sidebarOpen && <p className={styles.logoutBTN}>Logout</p>}
        </div>
      </div>
    </div>
  );
};

export default memo(Sidebar);
